import React, { useContext, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import styled from "styled-components";
import { mdiPencil } from "@mdi/js";
import commonUtils from "../../../../../../script/utils/commonUtils";

import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";
import attenuationUtils from "../../../../../../script/utils/attenuationUtils";

import { AttenuationDialog } from "./AttenuationDialog";
import { useInput } from "../../../../../hooks/useInput";
import { Button } from "../../../../../components/Button";
import { Checkbox } from "../../../../../components/Checkbox";
import { useOnChange } from "../../../../../hooks/useOnChange";
import { InputText } from "../../../../../components/InputText";
import { useValidation } from "../../../../../hooks/useValidation";
import { InputNumber } from "../../../../../components/InputNumber";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { SelectSingle } from "../../../../../components/SelectSingle";
import { ValidationProvider } from "../../../../../contexts/ValidationContext";

const AreaItemStyle = styled.div`
    >div:nth-child(1) {
        margin-bottom: 10px;
        display: grid;
        grid-template-columns: 2.5fr 1fr 1fr 1fr;
        gap: 8px;
        align-items: flex-end;
    }
    >div:nth-child(2) {
        gap: 8px;
        display: flex;
        align-items: flex-end;
        margin-bottom: 10px;
    }
`;

const roundTo2 = value => (value !== undefined && value !== null ? Math.round(value * 100) / 100 : value);

export const AreaItem = props => {
    const { index, plot, calcArea, rectangle } = props;

    const inputName = "area-" + index;

    const entryContext = useContext(EntryContext);
    const { entry } = entryContext;

    const [showAttenuationDialog, setShowAttenuationDialog] = useState(false);

    const calculateInclination = () => {
        if (calcArea.vaxisX !== undefined && calcArea.vaxisY !== undefined && calcArea.vaxisZ !== undefined) {
            return calcAreaUtils.calculateInclination(calcArea);
        }
        return undefined;
    };

    const initializeAttenuation = () => {
        if (calcArea.attenuationEnabled === true) {
            const { attenuation1, attenuation2, attenuationFrequency1, attenuationFrequency2 } = calcArea;
            const att = attenuationUtils.getAttenuation(attenuation1, attenuation2, attenuationFrequency1, attenuationFrequency2);
            if (att !== null) {
                return { attenuationClass: att.attenuationClass, thickness: att.thickness };
            } else if (!commonUtils.isBlank(attenuation1)) {
                return { attenuationClass: "Custom", thickness: null };
            }
        }
        return { attenuationClass: null, thickness: null };
    };

    const initializeHeight = () => {
        if (calcArea.originZ !== undefined && calcArea.vaxisZ !== undefined) {
            return roundTo2(calcArea.originZ + calcArea.vaxisZ);
        }
        return undefined;
    };

    const initializedHeight = initializeHeight();
    const calculatedInclination = calculateInclination();
    const attenuationValues = initializeAttenuation();

    const name = useInput(inputName + "-name", calcArea.name);
    const height = useInput(inputName + "-height", initializedHeight);
    const heightLow = useInput(inputName + "-heightLow", roundTo2(calcArea.originZ));
    const inclination = useInput(inputName + "-inclination", calculatedInclination);
    const attenuationEnabled = useInput(inputName + "-attenuationEnabled", calcArea.attenuationEnabled);
    const attenuationGlobal = useInput(inputName + "-attenuationGlobal", calcArea.attenuationGlobal);
    const attenuationClass = useInput(inputName + "-attenuationClass", attenuationValues.attenuationClass);
    const thickness = useInput(inputName + "-thickness", attenuationValues.thickness);
    const validationOptions = { globalErrorMessage: intl.get("entry.plan.areas.title") };

    const synchFields = useRef(commonUtils.isBlank(height.value) && commonUtils.isBlank(heightLow.value));

    useValidation(value => !commonUtils.isBlank(value), [name], validationOptions);
    useValidation(value => !commonUtils.isBlank(value), [height], validationOptions);
    useValidation(value => !commonUtils.isBlank(value) && (height.value === undefined || heightLow.value <= height.value), [heightLow, height], validationOptions);
    useValidation(() => inclination.value === undefined || inclination.value >= 0 && inclination.value <= 90, [inclination], validationOptions);
    useValidation(() => validateAttenuationFields(), [attenuationClass, attenuationEnabled], validationOptions);
    useValidation(() => validateAttenuationFields(), [thickness, attenuationEnabled], validationOptions);

    useOnChange((name, value) => {
        calcArea.name = value;
        entryContext.setEntry(entry);
    }, [name]);

    const onHeightChanged = value => {
        if (synchFields.current) {
            heightLow.set(value);
            heightLow.value = value;
            inclination.set(0);
        }
        if (heightLow.value !== undefined) {
            calcArea.vaxisZ = roundTo2(value - heightLow.value);
            calcArea.originZ = roundTo2(heightLow.value);
            calcArea.uaxisZ = 0;
            entryContext.setEntry(entry);
        }
    };

    const onHeightLowChanged = value => {
        calcArea.originZ = roundTo2(value);
        calcArea.uaxisZ = 0;
        if (height.value !== undefined) {
            calcArea.vaxisZ = roundTo2(height.value - value);
        }
        entryContext.setEntry(entry);
    };

    useOnChange((name, value) => {
        onHeightChanged(value);
        if (inclination.value < 90 && !inclination.ref.current.hasFocus) {
            inclination.set(calculateInclination(), true);
        }
    }, [height]);

    useOnChange((name, value) => {
        onHeightLowChanged(value);
        if (inclination.value < 90 && !inclination.ref.current.hasFocus) {
            inclination.set(calculateInclination(), true);
        }
    }, [heightLow]);

    const onInclinationChanged = () => {
        const {value} = inclination;
        if(commonUtils.isBlank(value)) {
            return;
        }

        if(value > 0) {
            calcArea.boundary = null;
        }

        if (value >= 0 && value < 90) {
            const { vaxisX, vaxisY } = calcAreaUtils.calculateArea(rectangle, plot);
            const lengthVFlat = Math.sqrt(vaxisX * vaxisX + vaxisY * vaxisY);
            const inclinationRadians = value / 180 * Math.PI;
            const vaxisZNotRounded = lengthVFlat * Math.tan(inclinationRadians);
            const vaxisZ = roundTo2(vaxisZNotRounded);
            calcArea.vaxisX = vaxisX;
            calcArea.vaxisY = vaxisY;
            if (height.value) {
                const value = roundTo2(height.value - vaxisZ);
                heightLow.set(value);
                onHeightLowChanged(value);
            } else if (heightLow.value) {
                const value = roundTo2(heightLow.value + vaxisZ);
                height.set(value);
                onHeightChanged(value);
            }
        } else if (value === 90) {
            calcArea.vaxisX = 0;
            calcArea.vaxisY = 0;
            calcArea.vaxisZ = roundTo2(height.value - heightLow.value);
            entryContext.setEntry(entry);
        }
    };

    useOnChange((name, value) => {
        calcArea.attenuationGlobal = value;
        entryContext.setEntry(entry);
    }, [attenuationGlobal]);

    useOnChange((name, value) => {
        calcArea.attenuationEnabled = value;
        if (value === false) {
            attenuationGlobal.set(false);
            calcArea.attenuationGlobal = false;
        } else {
            const attenuationValues = initializeAttenuation();
            attenuationClass.value = attenuationValues.attenuationClass;
            attenuationClass.set(attenuationValues.attenuationClass);
            thickness.value = attenuationValues.thickness;
            thickness.set(attenuationValues.thickness);
        }
        entryContext.setEntry(entry);
    }, [attenuationEnabled]);

    useOnChange(() => {
        if (attenuationClass.value === "Custom") {
            return;
        }
        const foundThickness = attenuationUtils.getThickness(attenuationClass.value, thickness.value);
        if (foundThickness === null) {
            thickness.set(null);
            calcArea.attenuation1 = null;
            calcArea.attenuationFrequency1 = null;
            calcArea.attenuation2 = null;
            calcArea.attenuationFrequency2 = null;
            entryContext.setEntry(entry);
        } else {
            calcArea.attenuation1 = foundThickness.attenuation1;
            calcArea.attenuationFrequency1 = foundThickness.attenuationFrequency1;
            calcArea.attenuation2 = foundThickness.attenuation2;
            calcArea.attenuationFrequency2 = foundThickness.attenuationFrequency2;
            entryContext.setEntry(entry);
        }
    }, [attenuationClass, thickness]);

    const validateAttenuationFields = () => {
        if (attenuationEnabled.value) {
            const { attenuation1, attenuationFrequency1, attenuation2, attenuationFrequency2 } = calcArea;
            if (commonUtils.isBlank(attenuation1)
                && commonUtils.isBlank(attenuationFrequency1)
                && commonUtils.isBlank(attenuation2)
                && commonUtils.isBlank(attenuationFrequency2)) {
                return false;
            }
        }
        return true;
    };

    const onConfirmAttenuationDialog = () => {
        entryContext.setEntry(entry);
        setShowAttenuationDialog(false);
    };

    const buildAttenuationClassLabel = val => {
        if (val === "c") { // custom
            return intl.get("entry.plan.areas.attenuationClassCustom");
        } else if (val === null || val === undefined) {
            return "";
        }
        return intl.get(`entry.plan.areas.attenuationClass${val}`);
    };

    useEffect(() => {
        //eventlisteners to detect when user is done editing height/heightLow fields
        const onHeightFocusOut = () => {
            synchFields.current = false;
        };
        const onHeightLowFocusOut = () => {
            synchFields.current = false;
        };
        const onInclinationFocusOut = () => onInclinationChanged();

        height.ref.current?.addEventListener("focusout", onHeightFocusOut);
        heightLow.ref.current?.addEventListener("focusout", onHeightLowFocusOut);
        inclination.ref.current?.addEventListener("focusout", onInclinationFocusOut);

        return () => {
            height.ref.current?.removeEventListener("focusout", onHeightFocusOut);
            heightLow.ref.current?.removeEventListener("focusout", onHeightLowFocusOut);
            inclination.ref.current?.removeEventListener("focusout", onInclinationFocusOut);
        };
    });

    return (
        <>
            <AreaItemStyle>
                <div>
                    <InputText {...name.bind}
                        maxLength="30"
                        title={intl.get("entry.plan.areas.name")} />
                    <InputNumber {...height.bind}
                        title={intl.get("entry.plan.areas.height")}
                        fractionDigits={2} />
                    <InputNumber {...heightLow.bind}
                        title={intl.get("entry.plan.areas.heightLow")}
                        fractionDigits={2} />
                    <InputNumber {...inclination.bind}
                        title={intl.get("entry.plan.areas.inclination")}
                        fractionDigits={2} />
                </div>
                <div>
                    <Checkbox {...attenuationEnabled.bind}
                        title={intl.get("entry.plan.areas.attenuation")} />
                    <Checkbox {...attenuationGlobal.bind}
                        title={intl.get("entry.plan.areas.global")}
                        disabled={!attenuationEnabled.value} />
                    <SelectSingle {...attenuationClass.bind}
                        title={intl.get("entry.plan.areas.attenuationClass")}
                        disabled={!attenuationEnabled.value}
                        toLabel={buildAttenuationClassLabel}>
                        {attenuationUtils.getAttenuationClasses()
                            .map(attenuationClass =>
                                <option key={attenuationClass} value={attenuationClass}>
                                    {attenuationClass === null ? "" : intl.get(`entry.plan.areas.attenuationClass${attenuationClass}`)}
                                </option>
                            )
                        }
                    </SelectSingle>
                    <SelectSingle {...thickness.bind}
                        title={intl.get("entry.plan.areas.thickness")}
                        disabled={!attenuationEnabled.value}
                        toLabel={value => (value ? String(value).replace(".", ",") : null)}>
                        {attenuationUtils.getThicknesses(attenuationClass.value)
                            .map(thickness =>
                                <option key={thickness} value={thickness}>
                                    {thickness ? String(thickness).replace(".", ",") : null}
                                </option>
                            )
                        }
                    </SelectSingle>
                    <Button
                        compact
                        svgIcon={mdiPencil}
                        disabled={!attenuationEnabled.value}
                        onClick={() => setShowAttenuationDialog(true)} />
                </div>
            </AreaItemStyle>
            {showAttenuationDialog &&
                <ValidationProvider>
                    <AttenuationDialog
                        attenuationClass={attenuationClass.value}
                        thickness={thickness.value}
                        calcArea={calcArea}
                        onConfirm={onConfirmAttenuationDialog}
                        onClose={() => setShowAttenuationDialog(false)}
                    />
                </ValidationProvider>
            }
        </>
    );
};